<template>
  <div
    ref="dropzoneEl"
    class="flowbuilder-dropzone"
    :data-dropzone-id="`${parentUuid}-${index}`"
    :data-parent-uuid="parentUuid"
    :data-index="index"
  >
    <!-- Large invisible hit area for easier drag targeting and clicking -->
    <div
      class="px-16 py-4 flex items-center justify-center transition-opacity duration-300 ease-in-out"
      :class="{
        'opacity-0 pointer-events-none': !isVisible,
        'opacity-100 cursor-pointer': isVisible && !isDraggingOver,
        'opacity-100': isVisible && isDraggingOver
      }"
      @dragover="handleDragOver"
      @dragenter="handleDragEnter"
      @dragleave="handleDragLeave"
      @drop="handleDrop"
      @click="handleClick"
    >
      <div
        class="p-1.5 shadow rounded-full transition-[color,background-color,box-shadow,transform] duration-200 w-8"
        :class="{
          'bg-white dark:bg-slate-700': !isDraggingOver && !isCloneMode,
          'bg-blue-500 dark:bg-blue-600 ring-4 ring-blue-200 dark:ring-blue-400/30 scale-125': isDraggingOver,
          'bg-amber-100 dark:bg-amber-900/60 ring-4 ring-amber-300/50 dark:ring-amber-700/30 animate-clone-pulse': isCloneMode && !isDraggingOver,
          'dark:text-white/90': !isDraggingOver && !isCloneMode,
          'text-white': isDraggingOver,
          'text-amber-600 dark:text-amber-400/80': isCloneMode && !isDraggingOver
        }"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke-width="1.5"
          stroke="currentColor"
          aria-hidden="true"
          data-slot="icon"
          class="w-5"
          :class="{
            'opacity-70': !isDraggingOver && !isCloneMode,
            'opacity-100 animate-pulse': isDraggingOver,
            'opacity-100': isCloneMode && !isDraggingOver
          }"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            d="M12 4.5v15m7.5-7.5h-15"
          />
        </svg>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, ref, onMounted, onUnmounted, type ComputedRef, type Ref } from 'vue'
import { uuid, deepCloneNode } from '../../services'

/**
 * Dropzone Component
 *
 * Provides drop targets between nodes for adding new nodes via drag and drop.
 * Shows a plus icon based on:
 * 1. When something is being dragged (for drag & drop functionality)
 * 2. When mouse is moving (fades in/out with cursor movement)
 * 3. When insert mode is active (press 'i' to toggle on/off)
 *
 * Also supports programmatic insertion via the 'dropzone-insert' custom event,
 * which is used by the Vimium-style keyboard hint system.
 */

const props = defineProps<{
  disabled?: boolean // Disable drop interaction (for unreachable nodes)
  parentUuid: string // UUID of the parent node containing this dropzone
  index: number // Position in parent's child array where dropped node will be inserted
}>()

const emit = defineEmits(['dropped-node'])

const adapter = inject('adapter') as any
const inRedirectFunnel = inject('inRedirectFunnel', false)

// Inject visibility state from FlowBuilder (mouse movement + insert mode)
const showDropzones = inject<ComputedRef<boolean>>('showDropzones', computed(() => false))

// Inject clone mode state
const cloneMode = inject<Ref<{ active: boolean, sourceNode: any } | null>>('cloneMode', ref(null))
const isCloneMode = computed(() => cloneMode.value?.active === true)

// Track if user is currently dragging over this specific dropzone
const isDraggingOver = ref(false)
const dragCounter = ref(0) // Counter to handle nested elements properly

// Check if we're currently dragging a node
const isDraggingNode = computed(() => {
  // Don't show as draggable if disabled
  if (props.disabled) return false

  if (!adapter?.isDragging?.value) return false

  const draggingItem = adapter.isDragging.value
  if (!draggingItem?.node) return false

  // In redirect funnels, restrict certain node types
  if (inRedirectFunnel) {
    const restrictedTypes = ['offer', 'exit']
    const isRestricted = restrictedTypes.includes(draggingItem.node.type) ||
      (draggingItem.node.type === 'sg' && draggingItem.node.ask === true)
    return !isRestricted
  }

  // In regular flows, restrict message nodes
  return draggingItem.node.type !== 'message'
})

// Combined visibility: show when dragging, mouse moving/insert mode, OR clone mode
const isVisible = computed(() => {
  if (props.disabled) return false
  return isDraggingNode.value || showDropzones.value || isCloneMode.value
})

const handleDragEnter = (event: DragEvent) => {
  event.preventDefault()
  event.stopPropagation()

  // Only show hover effect if this node type is allowed here
  if (!isDraggingNode.value) return

  dragCounter.value++
  isDraggingOver.value = true
}

const handleDragLeave = (event: DragEvent) => {
  event.preventDefault()
  event.stopPropagation()
  dragCounter.value--

  // Only set to false when completely leaving the dropzone
  if (dragCounter.value === 0) {
    isDraggingOver.value = false
  }
}

const handleDragOver = (event: DragEvent) => {
  event.preventDefault()
  event.stopPropagation()

  // Set dropEffect based on whether this node type is allowed
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = isDraggingNode.value ? 'copy' : 'none'
  }
}

const handleDrop = (event: DragEvent) => {
  event.preventDefault()
  event.stopPropagation()

  // Reset drag state
  isDraggingOver.value = false
  dragCounter.value = 0

  // Don't allow drops if disabled or node type not allowed
  if (props.disabled || !isDraggingNode.value) return

  const nodeData = event.dataTransfer?.getData('node')
  if (!nodeData) return

  try {
    const parsedData = JSON.parse(nodeData)

    // Handle both new nodes (direct node data) and existing nodes ({ node: data })
    const node = parsedData.node || parsedData

    // Check if this is an existing node being moved
    const isExisting = adapter?.isDragging?.value?.isExisting

    if (isExisting) {
      // For existing nodes, we want to move them, not create a copy
      emit('dropped-node', { node, isMove: true })
    } else {
      // For new nodes from the palette, ensure they have a UUID
      if (!node._uuid) {
        node._uuid = uuid()
      }
      emit('dropped-node', { node, isMove: false })
    }
  } catch (e) {
    console.error('Failed to parse dropped node data:', e)
  }
}

// ============================================
// Click to insert support
// ============================================

/**
 * Handle click on the dropzone to open the node insertion palette.
 * Uses the same mechanism as Vimium hints - passes the element reference
 * so the palette can dispatch the insert event back to this dropzone.
 */
const handleClick = (event: MouseEvent) => {
  event.preventDefault()
  event.stopPropagation()

  // Don't allow clicks if disabled
  if (props.disabled) return

  // If in clone mode, clone the source node and insert it here
  if (isCloneMode.value && cloneMode.value?.sourceNode) {
    const clonedNode = deepCloneNode(cloneMode.value.sourceNode)
    emit('dropped-node', { node: clonedNode, isMove: false, isClone: true })
    // Exit clone mode
    cloneMode.value = null
    return
  }

  // Call the adapter's click handler if available
  if (adapter?.onDropzoneClick && dropzoneEl.value) {
    adapter.onDropzoneClick({
      parentUuid: props.parentUuid,
      index: props.index,
      element: dropzoneEl.value,
      inRedirectFunnel: inRedirectFunnel  // Pass context for node filtering
    })
  }
}

// ============================================
// Programmatic insertion support (Vimium hints)
// ============================================

const dropzoneEl = ref<HTMLElement | null>(null)

/**
 * Handle programmatic node insertion via custom event.
 * This is triggered by the Vimium-style keyboard hint system.
 * Uses the same insertion path as drag & drop to ensure correct behavior.
 */
const handleProgrammaticInsert = (event: CustomEvent) => {
  // Don't allow insertion if disabled
  if (props.disabled) return

  const node = event.detail?.node
  if (!node) return

  // Ensure the node has a UUID
  if (!node._uuid) {
    node._uuid = uuid()
  }

  // Emit the same event as drag & drop
  emit('dropped-node', { node, isMove: false })
}

// Register/unregister event listener
onMounted(() => {
  if (dropzoneEl.value) {
    dropzoneEl.value.addEventListener('dropzone-insert', handleProgrammaticInsert as EventListener)
  }
})

onUnmounted(() => {
  if (dropzoneEl.value) {
    dropzoneEl.value.removeEventListener('dropzone-insert', handleProgrammaticInsert as EventListener)
  }
})
</script>
